import { useQuery } from '@tanstack/react-query';
import { getEvaluationJobStatus } from "../../services/evals";

interface UseEvaluationProgressOptions {
  jobId: string | null;
  enabled?: boolean;
  onComplete?: () => void;
}

export const useEvaluationProgress = ({
  jobId,
  enabled = true,
  onComplete,
}: UseEvaluationProgressOptions) => {
  const query = useQuery({
    queryKey: ["evaluationProgress", jobId],
    queryFn: () => getEvaluationJobStatus(jobId!),
    enabled: enabled && !!jobId,
    refetchInterval: query => {
      // Stop polling when job is completed or failed
      const data = query.state.data;
      if (data?.status === "completed" || data?.status === "failed") {
        // Call onComplete callback
        if (onComplete) {
          onComplete();
        }
        return false;
      }
      // Poll every 2 seconds while job is running
      return 2000;
    },
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to force refetch
  });

  const progress = query.data
    ? {
        total: query.data.total,
        completed: query.data.completed,
        successful: query.data.successful,
        failed: query.data.failed,
        percentage: query.data.total > 0 ? (query.data.completed / query.data.total) * 100 : 0,
      }
    : null;

  return {
    jobStatus: query.data,
    progress,
    isLoading: query.isPending,
    error: query.error,
  };
};
